import type { ElementRef, ReactElement } from "react"
import { Fragment, forwardRef, useImperativeHandle, useRef } from "react"

import { useTranslation } from "react-i18next"

import SearchIcon from "@mui/icons-material/Search"
import { useMediaQuery, useTheme } from "@mui/material"
import {
  Button,
  Checkbox,
  InputText,
  MenuItem,
  Pagination,
  Skeleton,
  Snackbar,
  Typography,
} from "@synapse-analytics/synapse-ui"

import type { SelectionProps, Variant } from "../Interfaces"

import styles from "./Selection.module.scss"

/**
 * The SelectionStep component is a dialog that appears when a user wants to import a workflow from the marketplace.
 * It displays a list of projects and their corresponding components. The user can select the components they want to import.
 * The component handles the state of the selected components and the current page of components.
 * It renders either a loading state or the list of components based on the props.
 * @param {Readonly<SelectionProps>} props - The props for the component.
 * @returns {ReactElement} - The rendered component.
 */
export const Selection = forwardRef(function (props, ref): ReactElement {
  const {
    formattedProjects,
    dialogTitle,
    componentsList,
    numOfTotalSelectedComponents,
    isProjectsLoading,
    isProjectsFetchingNextPage,
    isComponentsListLoading,
    setIsDialogOpen,
    searchInputHandler,
    importClickHandler,
    componentsCheckboxChangeHandler,
    selectedProject,
    setSelectedProject,
    selectedComponents,
    currentComponentsPage,
    setCurrentComponentsPage,
    setNumberOfPageItems,
    numberOfPageItems,
  } = props

  const MuiTheme = useTheme()

  const { t } = useTranslation("common")

  const componentsListRef = useRef<ElementRef<"div">>(null)

  useImperativeHandle(ref, () => {
    if (componentsListRef.current) return componentsListRef.current
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.secondaryHeaderContainer}>
        <Snackbar severity="info" fullWidth={useMediaQuery(MuiTheme.breakpoints.down("md"))}>
          {t("copyComponentFlow.selection.snackbar")}
        </Snackbar>
        <div className={styles.btnsContainer}>
          <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
            {t("copyComponentFlow.selection.cancelBtn")}
          </Button>
          <Button variant="primary" onClick={importClickHandler} disabled={!numOfTotalSelectedComponents}>
            {t("copyComponentFlow.selection.importBtn")}{" "}
            {numOfTotalSelectedComponents ? `(${numOfTotalSelectedComponents})` : ""}
          </Button>
        </div>
      </div>

      <section className={styles.contentContainer}>
        <section className={styles.projectsListContainer}>
          <div className={styles.searchBoxContainer}>
            <InputText
              handleChange={searchInputHandler}
              placeholder={t("copyComponentFlow.selection.searchPlaceHolder")}
              disabled={isProjectsLoading}
              variant="filled"
              type="search"
              hideDescription
              fullWidth
              startAdornment={
                <SearchIcon
                  fontSize="small"
                  style={{
                    paddingTop: "4px",
                    fill: "var(--base-text-2)",
                  }}
                />
              }
            />
          </div>

          <ul className={`scrollbar`}>
            {formattedProjects?.map((proj) => (
              <MenuItem
                key={proj.uuid}
                value={proj.uuid}
                onClick={() => {
                  setSelectedProject({ name: proj.name, id: proj.uuid })
                  setCurrentComponentsPage(1)
                }}
                isSelected={selectedProject?.id === proj.uuid}
                ref={proj.ref}
              >
                <div className={styles.menuItemContentContainer}>
                  <Typography variant="p">{proj.name}</Typography>
                  {(selectedComponents?.[proj.uuid]?.components?.length ?? 0) > 0 && (
                    <Typography variant="a">
                      <span className={styles.selectionCounter}>
                        ({selectedComponents?.[proj.uuid]?.components.length})
                      </span>
                    </Typography>
                  )}
                </div>
              </MenuItem>
            ))}

            {(isProjectsLoading || isProjectsFetchingNextPage) && (
              <div className={styles.skeletonsContainer}>
                <Skeleton width={"100%"} height={34} marginBottom={16} />
                <Skeleton width={"100%"} height={34} marginBottom={16} />
                <Skeleton width={"100%"} height={34} marginBottom={16} />
              </div>
            )}
          </ul>
        </section>

        <section className={styles.projectDataContainer}>
          <header>
            <Typography variant="h3-regular" variantColor={2}>
              {t(`copyComponentFlow.componentNames.${dialogTitle}`)}
            </Typography>
          </header>

          <div className={`${styles.componentsContainer} scrollbar`} ref={componentsListRef}>
            {isComponentsListLoading || isProjectsLoading ? (
              <div className={styles.paddingClass}>
                <Skeleton variant="rectangular" height={30} marginBottom={8} />
                <Skeleton variant="rectangular" height={30} marginBottom={8} />
                <Skeleton variant="rectangular" height={30} />
              </div>
            ) : componentsList?.count === 0 ? (
              <Typography variant="h3-bold" className={styles.paddingClass}>
                {t("copyComponentFlow.selection.emptyState", {
                  dialogTitle: t(`copyComponentFlow.componentNames.${dialogTitle}`),
                })}
              </Typography>
            ) : (
              <Fragment>
                <ul>
                  {componentsList?.results?.map((item) => (
                    <li key={item.uuid} className={styles.dataItem}>
                      <Checkbox
                        label={item.name}
                        value={item.uuid}
                        name={item.uuid}
                        onChange={componentsCheckboxChangeHandler.bind(null, item)}
                        checked={
                          !!selectedComponents?.[selectedProject?.id || ""]?.components.find((id) => id === item.uuid)
                        }
                      />
                    </li>
                  ))}
                </ul>

                <div className={styles.paginationContainer}>
                  <Pagination
                    page={currentComponentsPage}
                    onPageChange={(page) => {
                      setNumberOfPageItems(
                        componentsListRef.current?.clientHeight
                          ? //subtract the pagination element height and divide by item height
                            Math.floor((componentsListRef.current?.clientHeight - 48) / 38)
                          : 10,
                      )
                      setCurrentComponentsPage(page)
                    }}
                    pageSize={numberOfPageItems}
                    hidePageSize
                    totalCount={componentsList?.count}
                    variant={"complex"}
                  />
                </div>
              </Fragment>
            )}
          </div>
        </section>
      </section>
    </div>
  )
}) as <TVariant extends Variant>(
  props: SelectionProps<TVariant> & { ref?: React.ForwardedRef<ElementRef<"div"> | undefined> },
) => ReactElement
